import { useCallback, useEffect, useState } from "react";
import { Button, Icon, SkeletonText } from "@astrum/ui";
import { api } from "../../lib/api.js";
import type { Message } from "../../lib/api.js";
import { useRealtimeFrames } from "../../lib/realtime-context.js";

type Pin = { message: Message; pinnedAt: string; pinnedByName: string | null };

const PIN_PAGE = 10;

/**
 * The channel's pinned messages, newest pin first, for the details panel.
 * A click scrolls the conversation to the message. Refetched on a `pin` frame
 * so a colleague pinning something shows up without a reload.
 */
export function PinnedMessages({ channelId, onJump }: { channelId: string; onJump: (messageId: string) => void }) {
  const [pins, setPins] = useState<Pin[] | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const load = useCallback(() => {
    api
      .get<{ pins: Pin[]; hasMore: boolean }>(`/channels/${channelId}/pins?limit=${PIN_PAGE}`)
      .then((res) => {
        setPins(res.pins);
        setHasMore(res.hasMore);
      })
      .catch(() => setPins([]));
  }, [channelId]);

  useEffect(() => {
    setPins(null);
    load();
  }, [load]);

  useRealtimeFrames((frame) => {
    if (frame.t === "pin" && frame.channelId === channelId) load();
  });

  const loadMore = () => {
    const last = pins?.[pins.length - 1];
    if (!last || loadingMore) return;
    setLoadingMore(true);
    api
      .get<{ pins: Pin[]; hasMore: boolean }>(`/channels/${channelId}/pins?limit=${PIN_PAGE}&before=${last.message.id}`)
      .then((res) => {
        setPins((prev) => [...(prev ?? []), ...res.pins]);
        setHasMore(res.hasMore);
      })
      .catch(() => undefined)
      .finally(() => setLoadingMore(false));
  };

  return (
    <div className="chat-details__section">
      <h4>Pinned{pins && pins.length > 0 ? ` · ${pins.length}${hasMore ? "+" : ""}` : ""}</h4>
      {pins === null ? (
        <SkeletonText lines={2} />
      ) : pins.length === 0 ? (
        <p className="chat-details__hint">Nothing pinned yet. Pin a message from its hover menu.</p>
      ) : (
        <div className="chat-pins">
          {pins.map((pin) => (
            <button key={pin.message.id} type="button" className="chat-pins__item" onClick={() => onJump(pin.message.id)}>
              <Icon name="pin" size={12} />
              <span className="chat-pins__text">
                <strong>{pin.message.authorName}</strong>
                <span>{pin.message.body || "Message deleted"}</span>
              </span>
            </button>
          ))}
          {hasMore && (
            <Button size="sm" variant="ghost" loading={loadingMore} onClick={loadMore}>
              Show more
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
